import { useMemo, useState } from "react";
import { Note } from "../../../lib/api/models";
import { formatInCentralTime } from "../../../lib/formatting";
import Table from "../../common/tables/Table";
import { TableColumn } from "../../common/tables/Table/Table";
import styles from "./NotesTable.module.css";

const strings = {
  emptyNotesCritical:
    "A_0037 - No Critical Notes are available for this account.",
  emptyNotesExpanded:
    "A_0011 - No Critical or Regular notes match the current search criteria.",
  dateTime: "DATE / TIME",
  agent: "AGENT",
  ch: "CH",
  content: "CONTENT",
  source: "SOURCE",
  account: "ACCT",
};

const renderTableHeaderCell = (
  label: string,
  onClick: () => void,
  desc?: boolean,
) => (
  <button type="button" className={styles.headerCell} onClick={onClick}>
    {label}
    <div className={styles.sortIcon}>
      <div className={styles.sortIcon}>
        {desc === undefined || !desc ? (
          <div className={styles.triangleUp} />
        ) : null}
        {desc === undefined || desc ? (
          <div className={styles.triangleDown} />
        ) : null}
      </div>
    </div>
  </button>
);

type SortingOption =
  | "noteDate"
  | "agent"
  | "ch"
  | "content"
  | "source"
  | "accountNumberLast";

type SortBy = {
  option: SortingOption;
  desc: boolean;
};

const sortFns = {
  noteDate: (a: Note, b: Note) =>
    a.noteDate === b.noteDate ? 0 : a.noteDate > b.noteDate ? 1 : -1,
  agent: (a: Note, b: Note) =>
    a.agent === b.agent ? 0 : a.agent > b.agent ? 1 : -1,
  ch: (a: Note, b: Note) =>
    a.channel === b.channel ? 0 : a.channel > b.channel ? 1 : -1,
  content: (a: Note, b: Note) =>
    a.content === b.content ? 0 : a.content > b.content ? 1 : -1,
  source: (a: Note, b: Note) =>
    a.source === b.source ? 0 : a.source > b.source ? 1 : -1,
  accountNumberLast: (a: Note, b: Note) =>
    a.accountNumberLast === b.accountNumberLast
      ? 0
      : a.accountNumberLast > b.accountNumberLast
        ? 1
        : -1,
};

type Props = {
  notes: Note[];
  onClickRow: (note: Note) => void;
  notesPriority: "critical" | "expanded";
};

const longNoteThreshold = 1000;

export default function NotesTable({
  notes,
  onClickRow,
  notesPriority,
}: Props) {
  const [sortBy, setSortBy] = useState<SortBy>({
    option: "noteDate",
    desc: true,
  });

  const sortedData = useMemo((): Note[] => {
    return [...notes].sort(
      (a, b) => sortFns[sortBy.option](a, b) * (sortBy.desc ? -1 : 1),
    );
  }, [notes, sortBy]);

  const columns: TableColumn<Note>[] = [
    {
      header: renderTableHeaderCell(
        strings.dateTime,
        () =>
          setSortBy((prev) => {
            return {
              option: "noteDate",
              desc: prev.option !== "noteDate" ? false : !prev.desc,
            };
          }),
        sortBy.option === "noteDate" ? sortBy.desc : undefined,
      ),
      size: 2,
      alignment: "left",
      render: (row) =>
        formatInCentralTime(new Date(row.noteDate), "MM/dd/yyyy HH:mm"),
    },
    {
      header: renderTableHeaderCell(
        strings.agent,
        () =>
          setSortBy((prev) => {
            return {
              option: "agent",
              desc: prev.option !== "agent" ? false : !prev.desc,
            };
          }),
        sortBy.option === "agent" ? sortBy.desc : undefined,
      ),
      size: 1,
      alignment: "left",
      render: (row) => row.agent,
    },
    {
      header: renderTableHeaderCell(
        strings.ch,
        () =>
          setSortBy((prev) => {
            return {
              option: "ch",
              desc: prev.option !== "ch" ? false : !prev.desc,
            };
          }),
        sortBy.option === "ch" ? sortBy.desc : undefined,
      ),
      size: 1,
      alignment: "left",
      render: (row) => row.channel,
    },
    {
      header: renderTableHeaderCell(
        strings.content,
        () =>
          setSortBy((prev) => {
            return {
              option: "content",
              desc: prev.option !== "content" ? false : !prev.desc,
            };
          }),
        sortBy.option === "content" ? sortBy.desc : undefined,
      ),
      size: 6,
      alignment: "left",
      render: (row) => row.content,
    },
    {
      header: renderTableHeaderCell(
        strings.source,
        () =>
          setSortBy((prev) => {
            return {
              option: "source",
              desc: prev.option !== "source" ? false : !prev.desc,
            };
          }),
        sortBy.option === "source" ? sortBy.desc : undefined,
      ),
      size: 2,
      alignment: "left",
      render: (row) => row.source,
    },
    {
      header: renderTableHeaderCell(
        strings.account,
        () =>
          setSortBy((prev) => {
            return {
              option: "accountNumberLast",
              desc: prev.option !== "accountNumberLast" ? true : !prev.desc,
            };
          }),
        sortBy.option === "accountNumberLast" ? sortBy.desc : undefined,
      ),
      size: 2,
      alignment: "left",
      render: (row) => row.accountNumberLast,
    },
  ];

  return (
    <div className={styles.notesTableContainer}>
      <Table
        columns={columns}
        data={sortedData}
        className={styles.notesTable}
        onClickRow={onClickRow}
        isRowDisabled={(note: Note) => !note.detailId}
        isRowAlignStart={(note: Note) =>
          note.content.length > longNoteThreshold
        }
      />
      {sortedData.length === 0 ? (
        <div className={styles.emptyError}>
          {notesPriority === "critical"
            ? strings.emptyNotesCritical
            : strings.emptyNotesExpanded}
        </div>
      ) : null}
    </div>
  );
}
